import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, Trash2 } from "lucide-react";
import NavbarV3 from "../../../components/NavbarV3/NavbarV3.tsx";
import LoadingComponent from "../../../components/LoadingComponent/LoadingComponent.tsx";
import ToastErro from "../../../components/Toast/ToastErro/ToastErro.tsx";
import {
  deleteSolicitacaoAgregados,
  getSolicitacaoAgregados,
  postSolicitacaoAgregados,
} from "../../../services/ModuloGerenciador/SolicitacaoAgregados.js";
import { useProfile } from "../../../contexts/ProfileContext.tsx";
import MotionSemAutorizacao from "../../../core/components/MotionSemAutorizacao.tsx";
import { Tooltip } from "@mui/material";
import ItemNotFound from "../../../components/ItemNotFound/ItemNotFound.tsx";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

function maskCpfCnpj(value = "") {
  if (value.length <= 11) {
    return value
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }

  return value
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

function maskPhone(value = "") {
  value = value.replace(/\D/g, "");
  if (value.length <= 10) {
    return value.replace(/(\d{2})(\d{4})(\d)/, "($1) $2-$3");
  }
  return value.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
}

export default function SolicitacaoAgregados() {
  const { profile, hasRegra } = useProfile();
  const [loading, setLoading] = useState(true);
  const [agregados, setAgregados] = useState([]);
  const [textoErro, setTextoErro] = useState("");
  const [openToastErro, setOpenToastErro] = useState(false);
  const [semAutorizacao, setSemAutorizacao] = useState(false);

  const buscarAgregados = async () => {
    setLoading(true);
    await getSolicitacaoAgregados(profile?.token)
      .then((result) => {
        setAgregados(result.data);
        setLoading(false);
      })
      .catch((error) => {
        if (typeof error.response.data === "string") {
          setTextoErro(error.response.data);
        } else {
          setTextoErro("Erro inesperado. Tente novamente.");
        }
        setOpenToastErro(true);
        setLoading(false);
      });
  };

  const marcarAtendido = async (id, isJaAtendido) => {
    if (isJaAtendido) return;

    await postSolicitacaoAgregados(id, profile?.token)
      .then(() => buscarAgregados())
      .catch((error) => {
        if (typeof error.response.data === "string") {
          setTextoErro(error.response.data);
        } else {
          setTextoErro("Erro inesperado. Tente novamente.");
        }
        setOpenToastErro(true);
      });
  };

  const deletarItem = async (id) => {
    await deleteSolicitacaoAgregados(id, profile?.token)
      .then(() => buscarAgregados())
      .catch((error) => {
        if (typeof error.response.data === "string") {
          setTextoErro(error.response.data);
        } else {
          setTextoErro("Erro inesperado. Tente novamente.");
        }
        setOpenToastErro(true);
      });
  };

  useEffect(() => {
    if (profile?.token) {
      setSemAutorizacao(!hasRegra("Solicitação Agregados"));
      buscarAgregados();
    }
  }, [profile?.token]);

  const abrirWhats = (phoneNumber) => {
    const cleanedNumber = phoneNumber.replace(/\D/g, "");
    const formattedNumber = cleanedNumber.startsWith("55")
      ? cleanedNumber
      : `55${cleanedNumber}`;
    const url = `https://wa.me/${formattedNumber}`;
    window.open(url, "_blank");
  };

  if (semAutorizacao) {
    return <MotionSemAutorizacao />;
  }

  if (agregados && !agregados.length) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: 'column'
        }}
      >
        <NavbarV3 selecionado="SolicitacaoAgregados" title="Solicitações Agregados" />
        <div style={{ paddingTop: "200px" }}> <ItemNotFound label="Nenhum item foi localizado." /></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <NavbarV3 selecionado="SolicitacaoAgregados" title="Solicitações Agregados" />

      {loading && <LoadingComponent />}

      {!loading && (
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6"
          style={{ margin: "35px", alignItems: 'center', justifyContent: 'center' }}
        >
          {agregados.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-xl shadow border p-4 transition-all  ${item.isJaAtendido ? "bg-green-100 border-green-400" : "bg-blue-100"} h-[260px] justify-between`}
              style={{ width: 350 }}
            >
              <div>
                <div className='pointer' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: 'row', marginBottom: 5, gap: 10 }}>
                  <Tooltip title={<p>{item.nome}</p>}>
                    <h2
                      className="text-xl font-semibold"
                    >
                      {item.nome.length > 15 ? item.nome.substring(0, 15) + "..." : item.nome}
                    </h2>
                  </Tooltip>

                  <button
                    onClick={() => deletarItem(item.id)}
                    className="p-1 hover:bg-red-200 rounded-full bg-gray-100"
                  >
                    <Trash2 size={20} className="text-red-600" />
                  </button>
                </div>

                <div className="text-sm leading-5 gap-2" style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                  <p><strong>CPF/CNPJ:</strong> {maskCpfCnpj(item.cpfCnpj)}</p>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <p><strong>Telefone:</strong> {maskPhone(item.telefone)}</p>
                    {item.isWhatsApp && (
                      <span
                        onClick={() => abrirWhats(item.telefone)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="whatsapp-icon pointer"
                        title="Enviar mensagem no WhatsApp"
                        style={{ color: 'green' }}
                      >
                        <WhatsAppIcon size={24} color="#25D366" />
                      </span>
                    )}
                  </div>
                  <p><strong>Email:</strong> {item.email}</p>
                  <p><strong>Data:</strong> {new Date(item.data).toLocaleString()}</p>
                </div>
              </div>

              <button
                onClick={() => marcarAtendido(item.id, item.isJaAtendido)}
                disabled={item.isJaAtendido}
                className={`mt-4 flex items-center justify-center gap-2 px-4 py-2 rounded-lg shadow w-full
                  ${item.isJaAtendido
                    ? "bg-green-600 text-white opacity-70 cursor-not-allowed"
                    : "bg-blue-600 text-white"
                  }`}
              >
                <Check /> {item.isJaAtendido ? "Atendido" : "Marcar como atendido"}
              </button>
            </motion.div>
          ))}
        </div>
      )}

      <ToastErro
        texto={textoErro}
        open={openToastErro}
        onClose={() => setOpenToastErro(false)}
      />
    </motion.div>
  );
}
